import { memo, useLayoutEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import type { DateTime } from 'luxon';
import { motion, useReducedMotion } from 'motion/react';
import { useI18nContext } from '@/i18n/i18n-react';
import { IPhoneMockup } from '@/pages/onboarding/mockup/iphone-mockup';
import {
  KorzaStudioBackdrop,
  KorzaStudioBadge,
} from '@/pages/onboarding/mockup/korza-studio-backdrop';
import { NOTIFICATION_ENTRANCE_TRANSITION } from '@/pages/onboarding/mockup/notification-motion';
import { LockScreenScene } from '@/pages/onboarding/mockup/scenes/lock-screen-scene';
import { IosLockScreenNotification } from './ios-lock-screen-notification';
import {
  CONFIRM_ARRIVAL_MS,
  SAMPLE_LOCK_SCREEN_TIME,
  STUDIO_ENTRANCE_DELAY_MS,
  STUDIO_MAX_DEVICE_SCALE,
  fitDeviceScale,
  formatLockScreenClock,
  formatLockScreenDate,
  useDelayedFlag,
} from './korza-studio';
import { RollingClock } from './rolling-clock';

// IPhoneMockup's default; the notification is scaled from iOS points to it.
const SCREEN_WIDTH = 300;

// The lock screen scene is drawn for the onboarding's 300px screen (fixed
// clock and text sizes), so the whole device is scaled (down to fit short
// windows, up to STUDIO_MAX_DEVICE_SCALE on roomy ones) instead of being
// rendered at another `screenWidth`.
function useFitDeviceScale() {
  const stageRef = useRef<HTMLDivElement>(null);
  const deviceRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const device = deviceRef.current;
    if (!stage || !device) return;
    // offsetWidth/offsetHeight ignore the transform: the device's own size.
    const fit = () =>
      setScale(
        fitDeviceScale({
          stageWidth: stage.clientWidth,
          stageHeight: stage.clientHeight,
          deviceWidth: device.offsetWidth,
          deviceHeight: device.offsetHeight,
          max: STUDIO_MAX_DEVICE_SCALE,
        })
      );
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  return { stageRef, deviceRef, scale };
}

/** What was created, for the Studio's confirmation (see CONFIRM_*). */
export interface StudioConfirmation {
  /** Launch time, in the campaign's timezone. */
  at: DateTime;
  /** "Campanha agendada · Sai hoje, 19:00", "Campanha na fila · …". */
  label: string;
}

/**
 * Desktop composer preview in the onboarding's Korza Studio look: wavy
 * backdrop, "Studio" badge and an iPhone on the lock screen, where the push
 * arrives once and stays while the merchant writes (PUSH-05 AC 1/2: the text
 * updates on every keystroke). The notification itself is the iOS 26 one, cut and wrapped as
 * on an iPhone 17 (IosLockScreenNotification).
 *
 * With `confirmation` (the campaign was just created) the phone travels to
 * the launch: it goes to its always-on display with no notification, the
 * clock rolls to the launch time, then the screen wakes and the push rises
 * as customers will get it, with a confirmation pill.
 * Memoized: the form's own re-renders must not redo the phone.
 */
export const KorzaStudioPreview = memo(function KorzaStudioPreview({
  title,
  body,
  iconUrl,
  locale,
  confirmation = null,
}: {
  title: string;
  body: string;
  iconUrl: string | null | undefined;
  locale: string;
  confirmation?: StudioConfirmation | null;
}) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion() ?? false;
  const confirming = confirmation !== null;
  // Arrives once the column has painted, then stays while the merchant
  // writes (the text updates live); it only leaves on launch.
  const composedVisible = useDelayedFlag(
    true,
    reduceMotion ? 0 : STUDIO_ENTRANCE_DELAY_MS
  );
  const arrived = useDelayedFlag(
    confirming,
    reduceMotion ? 0 : CONFIRM_ARRIVAL_MS
  );
  const visible = confirming ? arrived : composedVisible;
  const { stageRef, deviceRef, scale } = useFitDeviceScale();
  const at = confirmation?.at ?? SAMPLE_LOCK_SCREEN_TIME;
  const dateLabel = formatLockScreenDate(at, locale);

  return (
    <section
      data-testid="composer-studio"
      data-confirmed={arrived || undefined}
      aria-label={LL.campaignComposer.preview.label()}
      className="relative h-full min-h-0 overflow-hidden border-l border-border/60"
    >
      <KorzaStudioBackdrop />
      {/* Absolute so the unscaled device never stretches the grid row. */}
      <div
        ref={stageRef}
        className="absolute inset-0 z-10 flex items-center justify-center"
      >
        <div ref={deviceRef} style={{ transform: `scale(${scale})` }}>
          <IPhoneMockup
            sceneKey="lock-screen"
            screenWidth={SCREEN_WIDTH}
            chromeBaked
          >
            <div className="relative h-full w-full">
              <div aria-hidden className="h-full w-full">
                <LockScreenScene
                  dimmed={confirming && !arrived}
                  dateLabel={
                    <motion.span
                      key={dateLabel}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: reduceMotion ? 0 : 0.5 }}
                      className="inline-block"
                    >
                      {dateLabel}
                    </motion.span>
                  }
                  clock={
                    <RollingClock value={formatLockScreenClock(at, locale)} />
                  }
                />
              </div>
              <IosLockScreenNotification
                title={title}
                body={body}
                titlePlaceholder={LL.notificationPreview.titlePlaceholder()}
                bodyPlaceholder={LL.notificationPreview.bodyPlaceholder()}
                timeLabel={LL.notificationPreview.now()}
                iconUrl={iconUrl}
                screenWidth={SCREEN_WIDTH}
                visible={visible}
                reduceMotion={reduceMotion}
              />
            </div>
          </IPhoneMockup>
        </div>
      </div>
      {confirmation && arrived && (
        // The form column announces the confirmation; this is its echo.
        <motion.div
          aria-hidden
          className="absolute inset-x-0 top-6 z-20 flex justify-center px-6"
          initial={reduceMotion ? false : { opacity: 0, y: -16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ ...NOTIFICATION_ENTRANCE_TRANSITION, delay: 0.15 }}
        >
          {/* Solid, not frosted: a blur over the animated waves repaints
              every frame. */}
          <span className="flex items-center gap-2 rounded-full bg-white py-1.5 pl-1.5 pr-3.5 text-sm font-medium text-foreground shadow-sm ring-1 ring-black/5">
            <span className="flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="size-3" strokeWidth={3} aria-hidden />
            </span>
            {confirmation.label}
          </span>
        </motion.div>
      )}
      <KorzaStudioBadge />
    </section>
  );
});
